import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ListChecks, MapPin, Layers, Wrench, ChevronRight, Tag } from "lucide-react";
import api from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import ActivityModal from "@/components/ActivityModal";
import ActivityResourcesDialog from "@/components/ActivityResourcesDialog";
import PrefCell from "@/components/PrefCell";
import Highlight from "@/components/Highlight";
import ListSearch from "@/components/ListSearch";
import { matchesQuery, buildHaystack } from "@/lib/search";
import { activityTypeColor, mergeActivityTypes } from "@/lib/activityTypes";
import { formatEuro } from "@/lib/resources";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PLACEMENT_META = {
  suelo: { label: "Suelos", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  pared: { label: "Paredes", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  techo: { label: "Techos", cls: "bg-violet-100 text-violet-800 border-violet-200" },
  espacio: { label: "Espacios", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  organizacion: { label: "Organización", cls: "bg-rose-100 text-rose-800 border-rose-200" },
};

const byPreference = (x, y) => {
  const px = x.preference || "", py = y.preference || "";
  if (px && py) { const c = px.localeCompare(py, "es", { numeric: true, sensitivity: "base" }); if (c !== 0) return c; }
  else if (px && !py) return -1;
  else if (!px && py) return 1;
  return x.name.localeCompare(y.name, "es", { sensitivity: "base" });
};

// Sub-agrupa las actividades de un nivel por Tipo de actividad (alfabético; "Sin tipo" al final).
const groupByType = (items) => {
  const m = {};
  for (const a of items) { const k = a.activity_type || "Sin tipo"; (m[k] = m[k] || []).push(a); }
  const keys = Object.keys(m).filter((k) => k !== "Sin tipo").sort((x, y) => x.localeCompare(y, "es", { sensitivity: "base" }));
  if (m["Sin tipo"]) keys.push("Sin tipo");
  return keys.map((k) => [k, m[k].slice().sort(byPreference)]);
};

export default function ModelActivities({ initialQuery = "" }) {
  const [activities, setActivities] = useState(null);
  const [areas, setAreas] = useState([]);
  const [levels, setLevels] = useState([]);
  const [units, setUnits] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [comoActivity, setComoActivity] = useState(null);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState(initialQuery || "");

  useEffect(() => {
    Promise.all([
      api.get("/model/activities"),
      api.get("/model/work-areas"),
      api.get("/model/levels"),
      api.get("/resource-units"),
    ]).then(([ac, ar, lv, un]) => { setActivities(ac.data); setAreas(ar.data); setLevels(lv.data); setUnits(un.data); })
      .catch(() => toast.error("No se pudieron cargar las actividades."));
  }, []);

  const areaMap = useMemo(() => { const m = {}; for (const a of areas) m[a.area_id] = a; return m; }, [areas]);
  const levelName = useMemo(() => { const m = {}; for (const l of levels) m[l.level_id] = l.name; return m; }, [levels]);

  // Agrupar actividades por nivel (el nivel lo hereda del área relacionada).
  const groups = useMemo(() => {
    const list = (activities || []).filter((a) =>
      (!typeFilter || a.activity_type === typeFilter) &&
      matchesQuery(search, buildHaystack(a, areaMap[a.area_id]?.name, levelName[areaMap[a.area_id]?.level_id], PLACEMENT_META[areaMap[a.area_id]?.placement]?.label, a.resource_names))
    );
    const result = (levels || []).map((l) => ({
      key: l.level_id,
      name: l.name,
      items: list.filter((a) => areaMap[a.area_id]?.level_id === l.level_id).sort(byPreference),
    }));
    const orphans = list.filter((a) => !areaMap[a.area_id] || !(areaMap[a.area_id].level_id in levelName)).sort(byPreference);
    if (orphans.length) result.push({ key: "__none__", name: "Sin nivel asignado", items: orphans });
    return result.filter((g) => g.items.length > 0);
  }, [activities, levels, areaMap, levelName, typeFilter, search]);

  const usedTypes = useMemo(
    () => Array.from(new Set((activities || []).map((a) => a.activity_type).filter(Boolean))).sort((x, y) => x.localeCompare(y, "es", { sensitivity: "base" })),
    [activities]
  );

  const sum3 = (list) => list.reduce((acc, a) => {
    acc[0] += a.subtotal_externa || 0; acc[1] += a.subtotal_interna || 0; acc[2] += a.subtotal_venta || 0; return acc;
  }, [0, 0, 0]);

  const typeTotals = useMemo(() => {
    const m = {};
    (activities || []).forEach((a) => {
      const t = a.activity_type || "Sin tipo";
      const agg = m[t] || [0, 0, 0];
      agg[0] += a.subtotal_externa || 0; agg[1] += a.subtotal_interna || 0; agg[2] += a.subtotal_venta || 0;
      m[t] = agg;
    });
    return { byType: m, grand: sum3(activities || []) };
  }, [activities]);

  const reloadActivities = () => api.get("/model/activities").then((r) => setActivities(r.data)).catch(() => {});

  const toggle = (k) => setCollapsed((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (act) => { setEditing(act); setModalOpen(true); };

  const onSaved = (saved, wasEditing) => {
    setActivities((list) => (wasEditing ? list.map((a) => (a.activity_id === saved.activity_id ? { ...a, ...saved } : a)) : [...list, saved]));
    reloadActivities();
  };

  const onAreaCreated = (area, unit) => {
    setAreas((list) => [...list, area]);
    if (unit && !units.includes(unit)) setUnits((u) => [...u, unit]);
  };

  const onAreaUpdated = (area) => setAreas((list) => list.map((a) => (a.area_id === area.area_id ? area : a)));

  const updatePreference = async (act, value) => {
    if ((value || "") === (act.preference || "")) return;
    const prev = act.preference || "";
    setActivities((list) => list.map((a) => (a.activity_id === act.activity_id ? { ...a, preference: value } : a)));
    try {
      await api.put(`/model/activities/${act.activity_id}`, { preference: value });
    } catch (e) {
      setActivities((list) => list.map((a) => (a.activity_id === act.activity_id ? { ...a, preference: prev } : a)));
      toast.error(e?.response?.data?.detail || "No se pudo actualizar la preferencia.");
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/model/activities/${toDelete.activity_id}`);
      setActivities((list) => list.filter((a) => a.activity_id !== toDelete.activity_id));
      toast.success("Actividad eliminada.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar.");
    } finally {
      setToDelete(null);
    }
  };

  if (!activities) return <LoadingScreen text="Cargando actividades…" />;

  const searching = search.trim().length > 0;

  const areaChip = (areaId) => {
    const a = areaMap[areaId];
    if (!a) return <span className="text-[11px] text-destructive">Área no encontrada</span>;
    const pm = PLACEMENT_META[a.placement];
    return (
      <span className="inline-flex items-center gap-1.5 flex-wrap text-[11px]">
        <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 font-medium"><MapPin className="h-3 w-3" /> {a.name}</span>
        {pm && <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${pm.cls}`}>{pm.label}</span>}
      </span>
    );
  };

  const renderRow = (act) => (
    <div key={act.activity_id} data-testid={`activity-row-${act.activity_id}`} className="group flex items-start gap-3 rounded-xl border border-border bg-card p-4 hover:bg-background-subtle transition-colors">
      <PrefCell value={act.preference} onSave={(v) => updatePreference(act, v)} testId={`activity-pref-input-${act.activity_id}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold truncate"><Highlight text={act.name} query={search} /></span>
          {act.activity_type && (
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${activityTypeColor(act.activity_type)}`} data-testid={`activity-type-badge-${act.activity_id}`}>
              <Tag className="h-2.5 w-2.5" /> {act.activity_type}
            </span>
          )}
        </div>
        {act.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{act.description}</p>}
        <div className="mt-2">{areaChip(act.area_id)}</div>
      </div>
      <div className="hidden sm:flex flex-col items-end shrink-0 text-right font-mono leading-tight mr-1" data-testid={`activity-subtotals-${act.activity_id}`}>
        <span className="text-sm font-semibold text-[#C75B28]">{formatEuro(act.subtotal_venta || 0)}</span>
        <span className="text-[10px] text-muted-foreground">ext {formatEuro(act.subtotal_externa || 0)} · int {formatEuro(act.subtotal_interna || 0)}</span>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button variant="outline" size="sm" className="h-8 rounded-full gap-1.5" onClick={() => setComoActivity(act)} data-testid={`como-activity-${act.activity_id}`}><Wrench className="h-3.5 w-3.5" /> CÓMO</Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(act)} data-testid={`edit-activity-${act.activity_id}`} aria-label="Editar"><Pencil className="h-4 w-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setToDelete(act)} data-testid={`delete-activity-${act.activity_id}`} aria-label="Eliminar"><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );

  return (
    <div data-testid="model-activities">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Actividades</h2>
          <p className="text-sm text-muted-foreground mt-1">Qué hay que hacer. Cada actividad se ubica en un área de trabajo (dónde) y hereda su nivel.</p>
        </div>
        <Button className="rounded-full gap-2" onClick={openNew} disabled={areas.length === 0} data-testid="new-activity-button">
          <Plus className="h-4 w-4" /> Nueva actividad
        </Button>
      </div>

      {areas.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="activities-no-areas">
          <MapPin className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Primero crea áreas de trabajo en <strong>¿DÓNDE?</strong>; después podrás añadir actividades y ubicarlas.</p>
        </div>
      ) : activities.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="activities-empty">
          <ListChecks className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Aún no hay actividades. Crea la primera para empezar a depurar el modelo.</p>
          <Button className="rounded-full gap-2 mt-4" onClick={openNew} data-testid="empty-new-activity-button"><Plus className="h-4 w-4" /> Nueva actividad</Button>
        </div>
      ) : (
        <>
          <ListSearch className="mb-4" value={search} onChange={setSearch}
            placeholder="Buscar por cualquier contenido…" testId="activity-search-input" />
          {usedTypes.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="activity-type-filter">
              <span className="text-xs text-muted-foreground mr-1">Tipo:</span>
              <button type="button" onClick={() => setTypeFilter("")} data-testid="activity-type-filter-all"
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${!typeFilter ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:bg-secondary"}`}>Todos</button>
              {usedTypes.map((t) => (
                <button type="button" key={t} onClick={() => setTypeFilter(typeFilter === t ? "" : t)} data-testid={`activity-type-filter-${t}`}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${typeFilter === t ? activityTypeColor(t) : "bg-card text-muted-foreground border-border hover:bg-secondary"}`}>{t}</button>
              ))}
            </div>
          )}
          <div className="mb-4 rounded-2xl border border-[#C75B28]/25 bg-[#C75B28]/[0.04] p-4" data-testid="activity-type-totals">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <span className="text-sm font-semibold text-[#C75B28]">Totales por tipo de actividad</span>
              <span className="text-xs font-mono text-muted-foreground">ext {formatEuro(typeTotals.grand[0])} · int {formatEuro(typeTotals.grand[1])} · <strong className="text-[#C75B28]">venta {formatEuro(typeTotals.grand[2])}</strong></span>
            </div>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {Object.entries(typeTotals.byType).sort((a, b) => a[0].localeCompare(b[0], "es")).map(([t, v]) => (
                <div key={t} className="flex items-center justify-between gap-2 text-xs font-mono bg-card rounded-lg border border-border px-3 py-1.5" data-testid={`type-total-${t}`}>
                  <span className="font-sans font-medium truncate">{t}</span>
                  <span className="text-muted-foreground shrink-0">ext {formatEuro(v[0])} · int {formatEuro(v[1])} · <strong className="text-[#C75B28]">venta {formatEuro(v[2])}</strong></span>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4" data-testid="activities-list">
          {groups.map((g) => {
            const isOpen = searching || !collapsed.has(g.key);
            const gt = sum3(g.items);
            return (
              <div key={g.key} data-testid={`activities-group-${g.key}`}>
                <button type="button" onClick={() => toggle(g.key)} data-testid={`activities-group-toggle-${g.key}`}
                  className="w-full flex items-center gap-2 mb-2 text-left group">
                  <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`} />
                  <Layers className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-sm font-semibold">{g.name}</span>
                  <span className="text-[11px] font-medium text-muted-foreground bg-secondary rounded-full px-2 py-0.5">{g.items.length}</span>
                  <span className="ml-auto text-[11px] font-mono text-[#C75B28] font-semibold" data-testid={`level-total-venta-${g.key}`}>{formatEuro(gt[2])}</span>
                </button>
                {isOpen && (
                  <div className="space-y-4 pl-1">
                    {groupByType(g.items).map(([tk, items]) => (
                      <div key={tk} data-testid={`activities-group-${g.key}-type-${tk}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tk === "Sin tipo" ? "text-muted-foreground border-border bg-secondary" : activityTypeColor(tk)}`}>
                            <Tag className="h-2.5 w-2.5" /> {tk}
                          </span>
                          <span className="text-[11px] font-medium text-muted-foreground">{items.length}</span>
                          <span className="flex-1 border-t border-border/60" />
                        </div>
                        <div className="space-y-2">{items.map(renderRow)}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-0.5 px-3 py-2 mt-1 text-[11px] font-mono text-muted-foreground border-t border-border/50" data-testid={`level-total-${g.key}`}>
                  <span className="mr-auto font-sans font-medium text-foreground/70">Total {g.name}</span>
                  <span>compra ext. {formatEuro(gt[0])}</span>
                  <span>compra int. {formatEuro(gt[1])}</span>
                  <span className="text-[#C75B28] font-semibold">venta {formatEuro(gt[2])}</span>
                </div>
              </div>
            );
          })}
          </div>
          {groups.length === 0 && (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="activities-filter-empty">
              Ninguna actividad coincide con la búsqueda o el filtro.
            </div>
          )}
        </>
      )}

      <ActivityModal open={modalOpen} onOpenChange={setModalOpen} areas={areas} levels={levels} levelName={levelName} units={units} activities={activities || []} activity={editing} onSaved={onSaved} onAreaCreated={onAreaCreated} onAreaUpdated={onAreaUpdated} />

      <ActivityResourcesDialog open={Boolean(comoActivity)} onOpenChange={(v) => { if (!v) setComoActivity(null); }} activity={comoActivity} activities={activities || []} onChanged={reloadActivities} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(v) => { if (!v) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar «{toDelete?.name}»?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-destructive hover:bg-destructive/90" onClick={confirmDelete} data-testid="confirm-delete-activity-button">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
